/**
 * Chart geometry + SVG strings for /precio-vivienda, shared by the build (static first
 * paint, what crawlers and no-JS readers get) and the browser (src/precios/interactive.ts
 * re-renders at the real width and adds the hover layer). Pure: no DOM, no fs.
 *
 * Rules (dataviz skill): one y-axis per chart, 2px lines, categorical slots in fixed
 * order (--viz-1 blue, --viz-2 orange, --viz-3 aqua; aqua is < 3:1 on white, so every
 * line also has a legend + end label and the page keeps a table view), diverging bars
 * blue up / red down from a zero baseline, text always in ink tokens.
 */

export type Slot = 1 | 2 | 3;

export interface LineSeries {
  key: string;
  label: string;
  slot: Slot;
  /** Aligned with LineSpec.x; null = no data that quarter (gap in the line). */
  values: (number | null)[];
}

export interface Marker {
  i: number;
  value: number;
  kind: 'peak' | 'trough' | 'now';
  label: string;
}

export interface LineSpec {
  x: string[];
  series: LineSeries[];
  markers?: Marker[];
  width?: number;
  height?: number;
  /** Unit after the tooltip / tick numbers. */
  unit?: string;
}

export interface LineLayout {
  width: number;
  height: number;
  pad: { l: number; r: number; t: number; b: number };
  lo: number;
  hi: number;
  tick: number;
  xAt: (i: number) => number;
  yAt: (v: number) => number;
  /** Index of the x position nearest to a pixel x. */
  indexAt: (px: number) => number;
}

const fmtInt = new Intl.NumberFormat('es-ES', { maximumFractionDigits: 0 });

export function niceTick(range: number, maxTicks = 6): number {
  const steps = [50, 100, 250, 500, 1000, 2000, 5000, 10000, 25000, 50000, 100000];
  return steps.find((t) => range / t <= maxTicks) ?? steps[steps.length - 1] ?? 1;
}

export function lineLayout(spec: LineSpec): LineLayout {
  const width = spec.width ?? 720;
  const height = spec.height ?? 300;
  const narrow = width < 520;
  const pad = { l: narrow ? 44 : 56, r: narrow ? 12 : 20, t: 14, b: 30 };
  const vals = spec.series.flatMap((s) => s.values.filter((v): v is number => v !== null));
  const minV = (vals.length ? Math.min(...vals) : 0) * 0.94;
  const maxV = (vals.length ? Math.max(...vals) : 1) * 1.04;
  const tick = niceTick(Math.max(1, maxV - minV), narrow ? 4 : 6);
  const lo = Math.max(0, Math.floor(minV / tick) * tick);
  const hi = Math.ceil(maxV / tick) * tick;
  const n = Math.max(1, spec.x.length - 1);
  const xAt = (i: number) => pad.l + (i / n) * (width - pad.l - pad.r);
  const yAt = (v: number) => pad.t + (1 - (v - lo) / Math.max(1, hi - lo)) * (height - pad.t - pad.b);
  const indexAt = (px: number) => Math.max(0, Math.min(spec.x.length - 1, Math.round(((px - pad.l) / (width - pad.l - pad.r)) * n)));
  return { width, height, pad, lo, hi, tick, xAt, yAt, indexAt };
}

/** SVG path for a series with gaps where values are null. */
export function linePath(values: readonly (number | null)[], L: Pick<LineLayout, 'xAt' | 'yAt'>): string {
  let d = '';
  let pen = false;
  values.forEach((v, i) => {
    if (v === null) {
      pen = false;
      return;
    }
    d += `${pen ? 'L' : 'M'}${L.xAt(i).toFixed(1)},${L.yAt(v).toFixed(1)}`;
    pen = true;
  });
  return d;
}

function xLabelEvery(count: number, width: number): number {
  // Quarterly x: label one year in N so labels never collide (~56px per label).
  const years = count / 4;
  const fit = Math.max(1, Math.floor((width - 80) / 56));
  return [1, 2, 3, 4, 5, 10].find((k) => years / k <= fit) ?? 10;
}

export function lineChartSvg(spec: LineSpec, ariaLabel: string): string {
  const L = lineLayout(spec);
  const { width, height, pad } = L;
  const grid: string[] = [];
  for (let v = L.lo; v <= L.hi + 1e-9; v += L.tick) {
    const y = L.yAt(v).toFixed(1);
    grid.push(
      `<line class="grid" x1="${pad.l}" x2="${width - pad.r}" y1="${y}" y2="${y}"/><text class="axis" x="${pad.l - 8}" y="${y}" text-anchor="end" dominant-baseline="middle">${fmtInt.format(v)}</text>`,
    );
  }
  const every = xLabelEvery(spec.x.length, width);
  const xs = spec.x
    .map((q, i) => ({ q, i }))
    .filter(({ q }) => q.endsWith('-T1') && Number(q.slice(0, 4)) % every === 0)
    .map(({ q, i }) => `<text class="axis" x="${L.xAt(i).toFixed(1)}" y="${height - 8}" text-anchor="middle">${q.slice(0, 4)}</text>`);
  const lines = spec.series.map((s) => `<path class="line s${s.slot}" d="${linePath(s.values, L)}"/>`);
  const marks = (spec.markers ?? []).map(
    (m) => `<circle class="mark ${m.kind}" cx="${L.xAt(m.i).toFixed(1)}" cy="${L.yAt(m.value).toFixed(1)}" r="5"/>`,
  );
  return `<svg viewBox="0 0 ${width} ${height}" width="${width}" height="${height}" role="img" aria-label="${esc(ariaLabel)}">${grid.join('')}${xs.join('')}${lines.join('')}${marks.join('')}<g class="hover" aria-hidden="true"></g></svg>`;
}

// ---------------------------------------------------------------------------- bars

export interface BarSpec {
  labels: string[];
  values: number[];
  width?: number;
  height?: number;
  /** Diverging (blue up / red down from 0) or one-hue magnitude. */
  diverging?: boolean;
  /** Tick formatter, e.g. percentages. */
  fmt?: (v: number) => string;
  /** Show a label under every Nth bar. */
  labelEvery?: number;
}

export interface BarLayout {
  width: number;
  height: number;
  pad: { l: number; r: number; t: number; b: number };
  band: number;
  barW: number;
  xAt: (i: number) => number;
  yAt: (v: number) => number;
  y0: number;
}

export function barLayout(spec: BarSpec): BarLayout {
  const width = spec.width ?? 720;
  const height = spec.height ?? 220;
  const pad = { l: width < 520 ? 44 : 56, r: 12, t: 12, b: 28 };
  const lo = Math.min(0, ...spec.values);
  const hi = Math.max(0, ...spec.values);
  const span = Math.max(1e-9, hi - lo);
  const band = (width - pad.l - pad.r) / Math.max(1, spec.values.length);
  // 2px surface gap between neighbours at minimum.
  const barW = Math.max(2, Math.min(28, band - Math.max(2, band * 0.25)));
  const xAt = (i: number) => pad.l + band * i + band / 2;
  const yAt = (v: number) => pad.t + ((hi - v) / span) * (height - pad.t - pad.b);
  return { width, height, pad, band, barW, xAt, yAt, y0: yAt(0) };
}

/** A bar with its data end rounded (4px) and its baseline end square. */
function barPath(x: number, w: number, y0: number, y1: number): string {
  const r = Math.min(4, w / 2, Math.abs(y1 - y0));
  const l = x - w / 2;
  const rt = x + w / 2;
  if (y1 <= y0) {
    return `M${l},${y0}V${y1 + r}Q${l},${y1} ${l + r},${y1}H${rt - r}Q${rt},${y1} ${rt},${y1 + r}V${y0}Z`;
  }
  return `M${l},${y0}V${y1 - r}Q${l},${y1} ${l + r},${y1}H${rt - r}Q${rt},${y1} ${rt},${y1 - r}V${y0}Z`;
}

export function barChartSvg(spec: BarSpec, ariaLabel: string): string {
  const L = barLayout(spec);
  const fmt = spec.fmt ?? ((v: number) => fmtInt.format(v));
  const lo = Math.min(0, ...spec.values);
  const hi = Math.max(0, ...spec.values);
  const tick = niceTickFloat(hi - lo);
  const grid: string[] = [];
  for (let v = Math.ceil(lo / tick) * tick; v <= hi + 1e-9; v += tick) {
    const y = L.yAt(v).toFixed(1);
    grid.push(
      `<line class="grid${Math.abs(v) < 1e-9 ? ' zero' : ''}" x1="${L.pad.l}" x2="${L.width - L.pad.r}" y1="${y}" y2="${y}"/><text class="axis" x="${L.pad.l - 8}" y="${y}" text-anchor="end" dominant-baseline="middle">${fmt(v)}</text>`,
    );
  }
  const every = spec.labelEvery ?? Math.max(1, Math.ceil(spec.labels.length / Math.max(1, Math.floor((L.width - 80) / 40))));
  const bars = spec.values.map((v, i) => {
    const cls = spec.diverging ? (v >= 0 ? 'bar pos' : 'bar neg') : 'bar s1';
    return `<path class="${cls}" data-i="${i}" d="${barPath(L.xAt(i), L.barW, L.y0, L.yAt(v))}"/>`;
  });
  const labels = spec.labels
    .map((t, i) => (i % every === 0 || i === spec.labels.length - 1 ? `<text class="axis" x="${L.xAt(i).toFixed(1)}" y="${L.height - 8}" text-anchor="middle">${esc(t)}</text>` : ''))
    .join('');
  return `<svg viewBox="0 0 ${L.width} ${L.height}" width="${L.width}" height="${L.height}" role="img" aria-label="${esc(ariaLabel)}">${grid.join('')}${bars.join('')}${labels}<g class="hover" aria-hidden="true"></g></svg>`;
}

function niceTickFloat(range: number): number {
  const raw = range / 4;
  const p = 10 ** Math.floor(Math.log10(Math.max(1e-9, raw)));
  return ([1, 2, 2.5, 5, 10].map((k) => k * p).find((t) => t >= raw) ?? 10 * p);
}

/** Tiny sparkline for a stat tile (no axes; the tile's numbers carry the values). */
export function sparklineSvg(values: readonly number[], width = 120, height = 36): string {
  if (values.length < 2) return '';
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  const xAt = (i: number) => 2 + (i / (values.length - 1)) * (width - 4);
  const yAt = (v: number) => 3 + (1 - (v - lo) / Math.max(1e-9, hi - lo)) * (height - 6);
  const d = values.map((v, i) => `${i ? 'L' : 'M'}${xAt(i).toFixed(1)},${yAt(v).toFixed(1)}`).join('');
  const last = values[values.length - 1] ?? 0;
  return `<svg class="spark" viewBox="0 0 ${width} ${height}" width="${width}" height="${height}" aria-hidden="true"><path class="line s1" d="${d}"/><circle class="mark now" cx="${xAt(values.length - 1).toFixed(1)}" cy="${yAt(last).toFixed(1)}" r="3"/></svg>`;
}

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
